import React from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router';

// Helpers
import { getURLSegment, promptUser, toggleDrawer } from '../../helpers/helpers.js'

// Translations
import translate from '../Translate.js';

// Dependencies
import DatePicker from 'react-datepicker';
import moment from 'moment';

// Components
import * as icons from '../Icons/Icons';
import Report from './Report';
import Graph from './Graph';
import Drawer from '../Drawer/Drawer';
import ProctorApprovals from './ProctorApprovals';

const Reports = observer(class Reports extends React.Component {
  constructor() {
    super();

    // Bind this to functions
    this.exportReport = this.exportReport.bind(this);
    this.completeExport = this.completeExport.bind(this);
    this.getReportData = this.getReportData.bind(this);
  }

  componentDidMount() {
    const { ReportStore } = this.props.store

    //API call to grab exams ranging from today to 30 days in the future
    if (getURLSegment(this, 2) === 'exams-by-week') {
      ReportStore.examsByWeekStart = moment().format('YYYY-MM-DD');
      ReportStore.examsByWeekEnd = moment().add(30,'d').format('YYYY-MM-DD');
      ReportStore.getExamsByWeekReport();
    }
  }

  componentWillMount() {
    const { AdminStore, ReportStore } = this.props.store;
    const _this = this;

    // Get settings and course offering data
    if(!AdminStore.institutionSettings.applicationSid) {
      AdminStore.getDefaultCourseOffering();
      AdminStore.getCourseOfferings();
    }

    // Reset report filters and state
    ReportStore.reportFilters = {};

    this.state = {
      drawerContent: null,
      drawerOpen: false,
      examSid: null,
      sessionSid: null,
      courseSid: null,
      startDate: null,
      endDate: null
    }

    // Get report data or reset if on landing page
    if(getURLSegment(this, 2)) {
      // Wait until settings have been loaded
      const settingsDelay = setInterval(function(){
        if(AdminStore.institutionSettings.applicationSid) {
          clearInterval(settingsDelay);
          ReportStore.getReportData(getURLSegment(_this, 2));
        }
      }, 100);
    } else if(getURLSegment(this, 1) === 'instructor-appointments') {
      // Wait until settings have been loaded
      const settingsDelay = setInterval(function(){
        if(AdminStore.institutionSettings.applicationSid) {
          clearInterval(settingsDelay);
          ReportStore.getReportData('instructor-appointments');
        }
      }, 100);
    } else {
      ReportStore.reportData = [];
    }
  }

  getReportData(e) {
    const { ReportStore } = this.props.store
    const { startDate, endDate } = this.state;
    const formattedStartDate = startDate.format("YYYY-MM-DD");
    const formattedEndDate = endDate.format("YYYY-MM-DD");

    e.preventDefault();
    ReportStore.getExamsByWeekReport(formattedStartDate, formattedEndDate);
  }

  exportReport() {
    const { translation } = this.props;
    const { ReportStore } = this.props.store;

    // Set the default export title
    if(getURLSegment(this, 1) === 'instructor-appointments') {
      ReportStore.reportExportTitle = translation.reports[getURLSegment(this, 1)].title;
    } else {
      ReportStore.reportExportTitle = translation.reports[getURLSegment(this, 2)].title;
    }

    // Show a dialog to let the user change the title
    const msg = {
      heading: translation.csv_prompt.heading,
      input: translation.csv_prompt.input,
      button: translation.csv_prompt.button
    }

    promptUser(msg, this.completeExport, null, ReportStore.reportExportTitle);
  }

  completeExport() {
    const { translation } = this.props;
    const { ReportStore, SessionStore } = this.props.store;

    const callback = () => SessionStore.showToaster(translation.exported, 'success');

    ReportStore.exportData(callback);
  }

  reportList() {
    const { userData } = this.props.store.SessionStore;

    let reports = [
      {
        url: 'exam-overview',
        icon: <icons.iconFiles />
      }, {
        url: 'exam-detail',
        icon: <icons.icon_text />
      }, {
        url: 'exam-volume',
        icon: <icons.iconScale />
      }, {
        url: 'course',
        icon: <icons.iconCap />
      }, {
        url: 'appointments',
        icon: <icons.iconCalendar />
      }, {
        url: 'automated-proctoring',
        icon: <icons.iconUnattended />
      }, {
        url: 'unscheduled-learners',
        icon: <icons.iconCalendarTimes />
      }, {
        url: 'exams-by-week',
        icon: <icons.iconInbox />
      }, {
        url: 'approvals',
        icon: <icons.iconUsers />
      }, {
        url: 'session-requests',
        icon: <icons.iconEnvelope />
      }, {
        url: 'incidents',
        icon: <icons.iconExclamation />
      }, {
        url: 'churn',
        icon: <icons.iconRandom />
      }, {
        url: 'written-audit',
        icon: <icons.iconPencil />
      }, {
        url: 'modality',
        icon: <icons.iconBarChart />
      }, {
        url: 'peak-exam',
        icon: <icons.iconClock />
      }
    ]

    if(userData && userData.role && userData.role.toLowerCase() === 'instructor') {
      reports = [
        {
          url: 'modality',
          icon: <icons.iconPencil />
        }
      ]
    }

    return reports;
  }

  render() {
    const { translation } = this.props;
    const { ReportStore } = this.props.store;
    const { graphs, reportData, reportTotal } = ReportStore;
    const reportList = this.reportList();

    return (
      <main tabIndex="0" id="main-content" className="page">
        <header className="page__header">
          <div className="wrap">
            {getURLSegment(this, 1) !== 'instructor-appointments' && (
              <h1 className="page__title">
                {translation.page_title}

                {getURLSegment(this, 2) &&
                  `: ${translation.reports[getURLSegment(this, 2)].title}`}
              </h1>
            )}

            {getURLSegment(this, 1) === 'instructor-appointments' && (
              <h1 className="page__title">
                {translation.reports.instructor_appointments}
              </h1>
            )}

            <div className="page__actions actions">
              {/* Hide the download button if there are no results */}
              {reportData &&
                reportData.length > 0 &&
                getURLSegment(this, 1) !== 'instructor-appointments' && (
                  <button className="btn--outline" onClick={this.exportReport}>
                    <icons.iconDownload />
                    {translation.csv_button}
                  </button>
                )}

              {/* Filter button */}
              {(getURLSegment(this, 2) ||
                getURLSegment(this, 1) === 'instructor-appointments') &&
                getURLSegment(this, 2) &&
                getURLSegment(this, 2) !== 'modality' && (
                  <button
                    className="btn"
                    onClick={() => {
                      this.setState(
                        {
                          filter: true
                        },
                        function () {
                          toggleDrawer(null, this, 'ReportFilter');
                        }
                      );
                    }}
                  >
                    <icons.iconSettings />
                    {translation.filter_button}
                  </button>
                )}

              {/* Exported CSVs button */}
              {!getURLSegment(this, 2) &&
                getURLSegment(this, 1) !== 'instructor-appointments' && (
                  <Link className="btn--outline" to="/reports/downloads">
                    {translation.downloads}
                  </Link>
                )}
            </div>
          </div>
        </header>

        {/* Date Picker for Exams by week */}
        {getURLSegment(this, 2) === 'exams-by-week' && (
          <form className="filter" onSubmit={this.getReportData}>
            <div className="wrap">
              <fieldset className="filter__filters">
                <ul className="fields fields--inline">
                  <li>
                    <label htmlFor="startDate" className="form-inline">
                      {translation.start_date}
                    </label>
                  </li>

                  <li>
                    <DatePicker
                      selected={this.state.startDate}
                      onChange={date => {
                        this.setState({ startDate: date });
                        ReportStore.examsByWeekStart = date.format('YYYY-MM-DD');
                      }}
                      name="startDate"
                      id="startDate"
                      dateFormat="YYYY-MM-DD"
                      autoComplete="off"
                    />
                  </li>

                  <li>
                    <label htmlFor="endDate" className="form-inline">
                      {translation.end_date}
                    </label>
                  </li>

                  <li>
                    <DatePicker
                      selected={this.state.endDate}
                      minDate={this.state.startDate}
                      onChange={date => {
                        this.setState({ endDate: date });
                        ReportStore.examsByWeekEnd = date.format('YYYY-MM-DD');
                      }}
                      name="endDate"
                      id="endDate"
                      dateFormat="YYYY-MM-DD"
                      autoComplete="off"
                    />
                  </li>
                </ul>
              </fieldset>

              <div className="actions">
                <button className="actions__btn actions__btn--primary">
                  {translation.get_report}
                </button>
              </div>
            </div>

          </form>
        )}

        <div className="wrap">
          {/* Report list */}
          {!getURLSegment(this, 2) &&
            getURLSegment(this, 1) !== 'instructor-appointments' && (
              <ul className="block-list block-list--grid">
                {reportList.map((report, i) => (
                  <li key={i}>
                    <Link
                      className="block-list__block block-list__link"
                      to={`${getURLSegment(this, 1)}/${report.url}`}
                    >
                      {report.icon}
                      <strong className="block-list__title">
                        {translation.reports[report.url].title}
                      </strong>

                      <div className="block__copy">
                        <p>{translation.reports[report.url].description}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

          {/* Report total */}
          {getURLSegment(this, 2) &&
            getURLSegment(this, 2) !== 'modality' &&
            getURLSegment(this, 2) !== 'peak-exam' && (
              <span className="page__subheading">
                {translation.total}: {reportTotal}
              </span>
            )}

          {/* General report component */}
          {(getURLSegment(this, 2) ||
            getURLSegment(this, 1) === 'instructor-appointments') &&
            reportData &&
            getURLSegment(this, 2) !== 'exams-by-week' &&
            getURLSegment(this, 2) !== 'approvals' &&
            getURLSegment(this, 2) !== 'modality' &&
            getURLSegment(this, 2) !== 'peak-exam' && (
              <Report reportData={reportData} parent={this} {...this.props} />
            )}

          {/* Proctor Approvals */}
          {getURLSegment(this, 2) &&
            reportData &&
            getURLSegment(this, 2) === 'approvals' && (
              <ProctorApprovals reportData={reportData} parent={this} {...this.props} />
            )}

          {/* Exams by week */}
          {getURLSegment(this, 2) &&
            reportData &&
            getURLSegment(this, 2) === 'exams-by-week' &&
            reportData.map((week, i) => {
              return (
                <Report
                  key={i}
                  reportData={week.exams}
                  parent={this}
                  disableScroll={true}
                  caption={`${week.start}–${week.end}`}
                  {...this.props}
                />
              );
            })}
        </div>

        {/* Modality Report */}
        {getURLSegment(this, 2) &&
          getURLSegment(this, 2) === 'modality' &&
          graphs.length > 0 &&
          graphs.map((graph, i) => <Graph {...this.props} key={i} data={graph.data} />)}

        {/* Peak Exam Report */}
        {getURLSegment(this, 2) &&
          getURLSegment(this, 2) === 'peak-exam' &&
          graphs.length > 0 &&
          graphs.map((graph, i) => {
            if (graph.data && graph.data.length) {
              return (
                <Graph
                  {...this.props}
                  key={i}
                  data={graph.data}
                  title={translation.graph_titles[graph.title]}
                  hideFilterBar
                />
              );
            } else if (i === 0) {
              return (
                <span className="no-results" key={i}>
                  {translation.no_results}
                </span>
              );
            }

            return null;
          })}

        {(getURLSegment(this, 2) !== 'approvals' || this.state.filter) && (
          <Drawer
            content={this.state.drawerContent}
            isOpen={this.state.drawerOpen}
            parent={this}
            examSid={this.state.examSid}
            sessionSid={this.state.sessionSid}
            courseSid={this.state.courseSid}
            {...this.props}
          />
        )}
      </main>
    );

    // if(!getURLSegment(this, 2) && getURLSegment(this, 1) !== 'instructor-appointments') {
    //   return ()
    // } else {
    //   return (
    //     <main tabIndex="0" id="main-content" className="page">
    //       <header className="page__header">
    //         <div className="wrap">
    //           {getURLSegment(this, 1) !== 'instructor-appointments' &&
    //             <h1 className="page__title">
    //               {translation.page_title}: {translation.reports[getURLSegment(this, 2)].title}
    //             </h1>
    //           }
    //
    //           {getURLSegment(this, 1) === 'instructor-appointments' &&
    //             <h1 className="page__title">
    //               {translation.reports.instructor_appointments}
    //             </h1>
    //           }
    //
    //           <div className="page__actions actions">
    //             {/* Hide the download button if there are no results */}
    //             {
    //               (
    //                 AdminStore.reportData.length > 0 ||
    //                 (getURLSegment(this, 2) === 'approvals' && AdminStore.approvals.length > 0)
    //               ) &&
    //               <button
    //                 className="btn--outline"
    //                 onClick={this.exportReport}
    //               >
    //                 <icons.iconDownload />
    //                 {translation.csv_button}
    //               </button>
    //             }
    //
    //             {getURLSegment(this, 2) !== 'exam' &&
    //               <button
    //                 className="btn"
    //                 onClick={() => toggleDrawer(null, this, 'ReportFilter')}
    //               >
    //                 <icons.iconSettings />
    //                 {translation.filter_button}
    //               </button>
    //             }
    //           </div>
    //         </div>
    //       </header>
    //
    //       {getURLSegment(this, 2) !== 'approvals' &&
    //         <div className="wrap">
    //           {AdminStore.reportData.length !== 0 &&
    //             <span className="page__subheading">
    //               {translation.total}: {AdminStore.reportTotal}
    //             </span>
    //           }
    //
    //           {
    //             AdminStore.reportData.length !== 0 &&
    //             !SessionStore.loading && (getURLSegment(this, 2) !== 'exam') &&
    //
    //             <Report
    //               data={this.props.store.AdminStore.reportData}
    //               parent={this}
    //               toggleDrawer={toggleDrawer}
    //               {...this.props}
    //             />
    //           }
    //           {
    //             AdminStore.reportData.length !== 0 &&
    //             !SessionStore.loading &&
    //             (getURLSegment(this, 2) === 'exam') &&
    //
    //             this.props.store.AdminStore.reportData.map((x, i) => {
    //               return (
    //                 <Report
    //                   key={i}
    //                   data={x.exams}
    //                   parent={this}
    //                   toggleDrawer={toggleDrawer}
    //                   disableScroll={true}
    //                   caption={`${x.start}—${x.end}`}
    //                   {...this.props}
    //                 />
    //               )
    //             })
    //           }
    //
    //           {AdminStore.reportData.length === 0 && !SessionStore.loading &&
    //             <span className="no-results">{translation.no_results}</span>
    //           }
    //         </div>
    //       }
    //
    //       {/* The proctor approvals report is quite different from the others, so we’ll use a separate component */}
    //       {getURLSegment(this, 2) === 'approvals' &&
    //         <ProctorApprovals {...this.props} />
    //       }
    //
    //       <Drawer
    //         content={this.state.drawerContent}
    //         isOpen={this.state.drawerOpen}
    //         parent={this}
    //         examSid={this.state.examSid}
    //         sessionSid={this.state.sessionSid}
    //         courseSid={this.state.courseSid}
    //         {...this.props}
    //       />
    //     </main>
    //   )
    // }
  }
})

export default translate('Reports')(Reports);



// WEBPACK FOOTER //
// ./src/app/components/Reports/Reports.js